import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { codeApi } from "core/api";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { Text } from "components/atoms/text";

type UsageRow = {
  id: number;
  created_at: string;
  user_id: string;
  user?: string;
  action: string;
  resource_id: number | null;
  num_links: number;
  credit_type: string;
  credit_amount: number;
  balance_before: number | null;
  balance_after: number | null;
};

const ACTION_LABEL: Record<string, string> = {
  search: "Ricerca",
  legal_practice: "Pratica legale",
  legal_file: "Report pratica",
};

const AdminUsage: FunctionComponent = () => {
  const [rows, setRows] = useState<UsageRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState("");
  const [action, setAction] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await codeApi.getUsageLog({
        user_id: userId || undefined,
        action: action || undefined,
        from: from ? `${from} 00:00:00` : undefined,
        to: to ? `${to} 23:59:59` : undefined,
        limit: 1000,
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userId, action, from, to]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container spacing={2} sx={{ p: 2 }}>
      <Grid item xs={12}>
        <Text variant="h4">Log attività e crediti</Text>
        <Text variant="body2">
          Chi ha fatto cosa: ricerche, pratiche e report, con n° di link e
          crediti scalati (saldo prima → dopo).
        </Text>
      </Grid>

      <Grid item xs={12} container spacing={1} alignItems="center">
        <Grid item>
          <TextField
            size="small"
            label="user_id"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            sx={{ minWidth: 240 }}
          />
        </Grid>
        <Grid item>
          <TextField
            size="small"
            select
            label="Azione"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Tutte</MenuItem>
            <MenuItem value="search">Ricerca</MenuItem>
            <MenuItem value="legal_practice">Pratica legale</MenuItem>
            <MenuItem value="legal_file">Report pratica</MenuItem>
          </TextField>
        </Grid>
        <Grid item>
          <TextField
            size="small"
            type="date"
            label="Dal"
            InputLabelProps={{ shrink: true }}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </Grid>
        <Grid item>
          <TextField
            size="small"
            type="date"
            label="Al"
            InputLabelProps={{ shrink: true }}
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={load} disabled={loading}>
            Aggiorna
          </Button>
        </Grid>
        <Grid item>
          <Text variant="body2">
            {loading ? "" : `${rows.length} righe`}
          </Text>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        {loading ? (
          <CircularProgress />
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data/ora</TableCell>
                  <TableCell>Utente</TableCell>
                  <TableCell>Azione</TableCell>
                  <TableCell>ID risorsa</TableCell>
                  <TableCell align="right">Link</TableCell>
                  <TableCell>Credito</TableCell>
                  <TableCell align="right">Scalato</TableCell>
                  <TableCell>Saldo prima→dopo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.created_at}</TableCell>
                    <TableCell>{r.user || r.user_id}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ACTION_LABEL[r.action] || r.action}
                      />
                    </TableCell>
                    <TableCell>{r.resource_id ?? "—"}</TableCell>
                    <TableCell align="right">{r.num_links}</TableCell>
                    <TableCell>
                      {r.credit_type === "none" ? "—" : r.credit_type}
                    </TableCell>
                    <TableCell align="right">
                      {r.credit_amount > 0 ? `-${r.credit_amount}` : "0"}
                    </TableCell>
                    <TableCell>
                      {r.balance_before == null && r.balance_after == null
                        ? "—"
                        : `${r.balance_before} → ${r.balance_after}`}
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Nessun dato
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
    </Grid>
  );
};

export default AdminUsage;
